import { inject } from '@angular/core';
import { type CanActivateFn, type CanMatchFn, Router, type UrlTree } from '@angular/router';

import { AUTH_CONFIG } from './auth-config.token';
import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';

/**
 * Route gate for the *authenticated* shell. Use as `canMatch` on the
 * parent route of the app so unauthenticated visitors never even
 * download the chunks for it.
 *
 * If the store is still `loading` (refresh probe in flight), we wait
 * for it to settle by running the boot refresh inline. After
 * `provideAppInitializer(authBoot)` lands, that step is a no-op
 * because the probe finished before bootstrap completed.
 */
export const authMatchGuard: CanMatchFn = async (): Promise<boolean | UrlTree> => {
  const store = inject(AuthStore);
  const auth = inject(AuthService);
  const config = inject(AUTH_CONFIG);
  const router = inject(Router);

  if (store.status() === 'loading') {
    await auth.bootRefresh();
  }
  if (store.isAuthenticated()) {
    return true;
  }
  return router.parseUrl(config.loginRoute);
};

/**
 * Inverse guard for the *login* route. Authenticated users hitting
 * `/login` get redirected to `postLoginRoute` instead.
 */
export const loginRedirectGuard: CanMatchFn = async (): Promise<boolean | UrlTree> => {
  const store = inject(AuthStore);
  const auth = inject(AuthService);
  const config = inject(AUTH_CONFIG);
  const router = inject(Router);

  if (store.status() === 'loading') {
    await auth.bootRefresh();
  }
  if (store.isAuthenticated()) {
    return router.parseUrl(config.postLoginRoute);
  }
  return true;
};

/** Factory: `hasRoleGuard('master_admin', 'office_admin')`. */
export function hasRoleGuard(...roleKeys: string[]): CanActivateFn {
  return (): boolean | UrlTree => {
    const store = inject(AuthStore);
    const router = inject(Router);
    const config = inject(AUTH_CONFIG);
    if (store.hasRole(...roleKeys)) return true;
    return router.parseUrl(config.postLoginRoute);
  };
}

/** Factory: `hasPermissionGuard('customer:write')`. */
export function hasPermissionGuard(...permissions: string[]): CanActivateFn {
  return (): boolean | UrlTree => {
    const store = inject(AuthStore);
    const router = inject(Router);
    const config = inject(AUTH_CONFIG);
    if (store.hasPermission(...permissions)) return true;
    return router.parseUrl(config.postLoginRoute);
  };
}
